/**
 * «Complete your routine» / «Completa tu rutina» panel in the cart drawer (WI-1772):
 * the add buttons. Markup: snippets/cart-complements.liquid.
 *
 * Loaded ONCE per page from layout/theme.liquid (only while the setting is on), never from
 * inside the drawer: the drawer is re-rendered with innerHTML on every cart change, and a
 * <script> inserted that way never runs (the inline handler in
 * snippets/shoppable-product-card.liquid has exactly that bug). So the listener is
 * delegated on document and survives every re-render.
 *
 * Each card is a real <form action="/cart/add">. Without window.cmCartAdd the form posts
 * natively (lands on /cart, line property included). With it, the add goes through
 * cmCartAdd, which re-renders the drawer and, since the cm-cart-add fix, keeps it open.
 *
 * After a successful add the drawer can be re-rendered once more by the Shipping Protection
 * reconcile (snippets/cm-insurance.liquid): 350 ms after the add settles it reads /cart.js
 * and, when the insurance quantity has to change, writes /cart/update.js and re-renders.
 * While that is pending it flags <cart-drawer> with .cm-ins-busy. Focus, the drawer's tab
 * order and the «added» announcement are kept usable until that flag clears.
 */
(function () {
  if (window.__cmCartComplements) return;
  window.__cmCartComplements = true;

  var FORM = "form[data-cm-cc-form]";
  var PANEL = "[data-cm-cc]";
  var ADD = "[data-cm-cc-add]";
  var REGION_ID = "CartDrawer-LiveRegionText";
  var INSURANCE_BUSY = "cm-ins-busy";

  // A click that lands on the NEXT suggestion right after the re-render (a slow double
  // click, an impatient second tap) is not a choice: ignore activations for this long after
  // a successful add.
  var COOLDOWN_MS = 700;
  // Watch for the insurance re-render while .cm-ins-busy is on, but never longer than this:
  // the flag stays on if one of its requests fails.
  var WATCH_MAX_MS = 15000;
  // Without the insurance flag nothing else is pending; watch briefly, as before.
  var WATCH_SHORT_MS = 4000;
  // A live region that is already filled when it appears is not announced: write a beat
  // after the re-render, and after the insurance re-render if one is pending (up to a cap).
  var ANNOUNCE_DELAY_MS = 150;
  var ANNOUNCE_WAIT_MS = 2000;
  var ANNOUNCE_REWRITE_MS = 250;

  var coolUntil = 0;
  var watcher = null;
  var notice = null;
  var noticeTimer = null;

  function getDrawer() {
    return document.querySelector("cart-drawer");
  }

  function isOpen(drawer) {
    return !!drawer && drawer.hasAttribute("open");
  }

  function isRendered(el) {
    return !!el && el.getClientRects().length > 0;
  }

  function setOnAll(nodes, name, value) {
    for (var i = 0; i < nodes.length; i++) {
      if (value === null) nodes[i].removeAttribute(name);
      else nodes[i].setAttribute(name, value);
    }
  }

  // The drawer's own controls are server-rendered with tabindex="-1" and only switched to 0
  // when the drawer toggles open, so a re-render while it stays open leaves them out of the
  // tab order. Same helper and selector list the drawer uses (assets/global.js,
  // data-toggle-tabindex in snippets/cart-drawer.liquid).
  function restoreTabOrder(drawer) {
    if (typeof setTabindex === "function" && typeof drawer.toggleEelements === "function") {
      setTabindex(drawer.toggleEelements(), "0");
    }
  }

  // The clicked button no longer exists after the re-render, so focus fell back to <body>.
  // Put it on the button now in the same slot (the next suggestion), or on the drawer's
  // close button when the panel is gone. Never steals focus the shopper has moved.
  // Only a keyboard add scrolls to it: with a tap the re-rendered list stays at the top,
  // where the new line has just appeared.
  function restoreFocus(drawer, add) {
    var active = document.activeElement;
    if (active && active !== document.body) return;
    var panel = drawer.querySelector(PANEL + '[data-placement="' + add.placement + '"]');
    var buttons = panel ? panel.querySelectorAll(ADD) : [];
    var target = buttons.length ? buttons[Math.min(add.index, buttons.length - 1)] : null;
    if (!isRendered(target)) target = drawer.querySelector(".wt-cart__drawer__close");
    if (target && typeof target.focus === "function") target.focus({ preventScroll: !add.keyboard });
  }

  // Announcements go to the drawer's own role=status region. It lives inside .drawer__inner,
  // so every re-render brings a NEW, empty node. The text is written once per region node:
  // ANNOUNCE_DELAY_MS after the re-render, or once the insurance reconcile is done (capped
  // at ANNOUNCE_WAIT_MS), and written again only if a later re-render replaced the node it
  // was written to (see keepUsableAfterAdd).
  function announce(text) {
    if (!text) return;
    notice = { text: text, until: Date.now() + ANNOUNCE_WAIT_MS, region: null };
    scheduleNotice(ANNOUNCE_DELAY_MS);
  }

  function scheduleNotice(delay) {
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(flushNotice, delay);
  }

  function flushNotice() {
    if (!notice) return;
    var drawer = getDrawer();
    if (!isOpen(drawer)) {
      notice = null;
      return;
    }
    if (drawer.classList.contains(INSURANCE_BUSY) && Date.now() < notice.until) {
      scheduleNotice(ANNOUNCE_DELAY_MS);
      return;
    }
    var region = document.getElementById(REGION_ID);
    if (!region || region === notice.region) return;
    notice.region = region;
    region.setAttribute("aria-hidden", "false");
    region.textContent = notice.text;
  }

  function stopWatching() {
    if (!watcher) return;
    watcher.observer.disconnect();
    clearTimeout(watcher.timer);
    watcher = null;
  }

  // Each re-render after an add drops focus and the drawer's tab order. Watch the drawer
  // until the insurance reconcile is done (.cm-ins-busy cleared), the drawer closes, or the
  // cap; not on a fixed clock, which a slow network outlives.
  function keepUsableAfterAdd(add) {
    stopWatching();
    var drawer = getDrawer();
    var root = document.getElementById("CartDrawer");
    if (!drawer || !root || typeof MutationObserver !== "function") return;
    var tied = drawer.classList.contains(INSURANCE_BUSY);
    var observer = new MutationObserver(function () {
      var current = getDrawer();
      if (!isOpen(current)) {
        stopWatching();
        return;
      }
      restoreTabOrder(current);
      restoreFocus(current, add);
      // Not written yet, or written into a region this re-render replaced: (re)start the
      // delay from now, so the text never lands in the frame its region appears.
      if (notice && document.getElementById(REGION_ID) !== notice.region) {
        scheduleNotice(notice.region ? ANNOUNCE_REWRITE_MS : ANNOUNCE_DELAY_MS);
      }
      if (tied && !current.classList.contains(INSURANCE_BUSY)) stopWatching();
    });
    // childList on the re-rendered subtree, plus the drawer's own open/class, which carry the
    // close and .cm-ins-busy. No loop: setTabindex only changes attributes, and the
    // announcement's own text change finds its region already written.
    observer.observe(root, { childList: true, subtree: true });
    observer.observe(drawer, { attributes: true, attributeFilter: ["class", "open"] });
    watcher = {
      observer: observer,
      timer: setTimeout(stopWatching, tied ? WATCH_MAX_MS : WATCH_SHORT_MS),
    };
  }

  // The card's form as it is NOW: a re-render (an insurance reconcile, a quantity change)
  // can replace the panel while an add is in flight.
  function liveForm(form, placement, id) {
    if (document.contains(form)) return form;
    var input = document.querySelector(
      PANEL + '[data-placement="' + placement + '"] ' + FORM + ' input[name="id"][value="' + id + '"]'
    );
    return input ? input.form : null;
  }

  document.addEventListener("submit", function (event) {
    if (event.defaultPrevented) return; // someone else already handled this submit
    var form = event.target;
    if (!form || typeof form.matches !== "function" || !form.matches(FORM)) return;
    if (typeof window.cmCartAdd !== "function") return; // no helper → native POST to /cart/add

    var idInput = form.querySelector('input[name="id"]');
    var id = idInput ? parseInt(idInput.value, 10) : NaN;
    if (!id) return;
    event.preventDefault();
    if (Date.now() < coolUntil) return;

    var panel = form.closest(PANEL);
    var button = form.querySelector(ADD);
    if (!panel || !button || panel.hasAttribute("data-cm-cc-busy")) return;

    var item = { id: id, quantity: 1 };
    var source = form.querySelector('input[name="properties[_cm_src]"]');
    if (source && source.value) item.properties = { _cm_src: source.value };

    var buttons = panel.querySelectorAll(ADD);
    var add = {
      placement: panel.getAttribute("data-placement") || "",
      index: Math.max(0, Array.prototype.indexOf.call(buttons, button)),
      keyboard: false,
    };
    try {
      add.keyboard = button.matches(":focus-visible");
    } catch (e) {}
    var errorText = panel.getAttribute("data-error") || "";
    var addedText = panel.getAttribute("data-added") || "";
    var errorBox = form.querySelector("[data-cm-cc-error]");
    if (errorBox) errorBox.textContent = "";

    // Busy: one add at a time. Every button of the panel is declared inactive (aria-disabled,
    // not disabled, so the pressed one keeps focus) and the pressed one is named by what it
    // shows («Adding…»), not «Add X to cart».
    var idleLabel = button.getAttribute("aria-label");
    var busyLabel = button.querySelector(".cm-cc__add-busy");
    busyLabel = busyLabel ? busyLabel.textContent.replace(/\s+/g, " ").trim() : "";
    panel.setAttribute("data-cm-cc-busy", "");
    setOnAll(buttons, "aria-disabled", "true");
    button.setAttribute("aria-busy", "true");
    if (busyLabel) button.setAttribute("aria-label", busyLabel);

    function release() {
      panel.removeAttribute("data-cm-cc-busy");
      setOnAll(buttons, "aria-disabled", null);
      button.removeAttribute("aria-busy");
      if (idleLabel) button.setAttribute("aria-label", idleLabel);
    }

    window.cmCartAdd([item]).then(
      function () {
        // cmCartAdd has already re-rendered the drawer (this panel included) or is taking
        // the shopper to /cart. Old nodes are only still here if nothing re-rendered.
        coolUntil = Date.now() + COOLDOWN_MS;
        if (document.contains(panel)) release();
        var drawer = getDrawer();
        if (!isOpen(drawer)) return;
        restoreFocus(drawer, add);
        announce(addedText);
        keepUsableAfterAdd(add);
      },
      function () {
        // 422 (sold out since the render, quantity limit) or network. Nothing was added, but
        // something else may have re-rendered the panel meanwhile: write the message next to
        // the same product as it is now, or announce it if that card is gone.
        release();
        var target = liveForm(form, add.placement, id);
        var box = target ? target.querySelector("[data-cm-cc-error]") : null;
        if (box) box.textContent = errorText;
        else announce(errorText);
      }
    );
  });
})();
